// ════════════════════════════════════════════════════════════════════════
//  Ergebnisübersicht
//
//  Der Fortschritt der Klasse, gelesen gegen die LEHRGÄNGE der Klasse - nicht
//  gegen die neuesten. Sonst stünden in der Tabelle Lektionen, die diese Kinder
//  nie gesehen haben.
//
//  Die Lehrgänge holt `useClassCourses` in features/courses; hier bleibt der
//  Fortschritt. Was ein Kind geübt hat, hängt an Stufenschlüsseln und weiß
//  nichts von Lehrgängen - die Zuordnung entsteht erst beim Anzeigen.
// ════════════════════════════════════════════════════════════════════════

import { useQuery } from "@tanstack/react-query";
import type { Bundle } from "@otto/lehrgang";
import { queryKeys, type OttoClient } from "@otto/plattform";

export type StageMark = {
  readonly stars: 1 | 2 | 3;
  /** Echte Genauigkeit, nicht aus den Sternen zurückgerechnet – siehe Kommentar
   *  in Ergebnisse.tsx: stars/3 wäre eine falsche Näherung. */
  readonly accuracy: number;
  readonly updated: string;
};

/** Eine Messung des Eingangs- oder Abschlusstests. */
export type Measurement = {
  readonly testKey: string;
  readonly phase: "vorher" | "nachher";
  readonly chars: number;
  readonly seconds: number;
  readonly accuracy: number;
};

export type StudentRow = {
  readonly code: string;
  /** Messungen dieses Kindes, über alle Lehrgänge – zugeordnet wird beim Anzeigen. */
  readonly tests: readonly Measurement[];
  /** stageKey → Stand. Nur Stufen, die das Kind geübt hat. */
  readonly stages: Readonly<Record<string, StageMark>>;
  readonly lastAt: string | null;
};

/**
 * Stufenschlüssel mit Fortschritt, die es in keinem Lehrgang der Klasse (mehr)
 * gibt.
 *
 * Reine Rechnung und nicht Teil der Abfrage: sie hängt an den Lehrgängen, die
 * Abfrage nur an der Klasse. Steckte sie in der Abfrage, müsste deren Schlüssel
 * die Lehrgänge mittragen - und zwei Ansichten derselben Zeilen lägen zweimal
 * im Cache.
 *
 * Verwaist ist eine Stufe erst, wenn sie in KEINEM Bundle vorkommt. Sonst
 * meldete jeder zweite Lehrgang die Übungen des ersten als verloren.
 */
export function orphanKeys(
  rows: readonly StudentRow[],
  bundles: readonly Bundle[],
): string[] {
  const known = new Set(
    bundles.flatMap((b) => b.lessons.flatMap((l) => l.stages.map((s) => s.key))),
  );
  const orphan = new Set<string>();
  for (const row of rows) {
    for (const key of Object.keys(row.stages)) if (!known.has(key)) orphan.add(key);
  }
  return [...orphan].sort();
}

export function useResults(client: OttoClient, classId: string) {
  return useQuery({
    queryKey: queryKeys.results(classId),
    enabled: classId !== "",
    queryFn: async (): Promise<StudentRow[]> => {
      const { data, error } = await client
        .from("students")
        .select(
          "code, progress(lesson_key, stage_key, stars, accuracy, updated_at), test_results(test_key, phase, chars, seconds, accuracy)",
        )
        .eq("class_id", classId)
        .order("code");
      if (error) throw error;

      return (data ?? []).map((student) => {
        const entries = (student.progress ?? []) as {
          stage_key: string;
          stars: number;
          accuracy: number | string;
          updated_at: string;
        }[];
        const stages: Record<string, StageMark> = {};
        let lastAt: string | null = null;

        for (const entry of entries) {
          stages[entry.stage_key] = {
            // Die Tabelle erlaubt 0..3, die RPC schreibt nur 1..3. Hier wird
            // geklemmt statt gecastet: eine 0 aus einer alten Zeile soll die
            // Anzeige nicht in einen unmöglichen Zustand bringen.
            stars: Math.min(3, Math.max(1, entry.stars)) as 1 | 2 | 3,
            // numeric kommt als Text über PostgREST.
            accuracy: Number(entry.accuracy),
            updated: entry.updated_at,
          };
          if (lastAt === null || entry.updated_at > lastAt) lastAt = entry.updated_at;
        }

        const tests = (
          (student.test_results ?? []) as {
            test_key: string;
            phase: "vorher" | "nachher";
            chars: number;
            seconds: number;
            accuracy: number | string;
          }[]
        ).map((row) => ({
          testKey: row.test_key,
          phase: row.phase,
          chars: row.chars,
          seconds: row.seconds,
          accuracy: Number(row.accuracy),
        }));

        return { code: student.code, stages, tests, lastAt };
      });
    },
  });
}
